import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from "@angular/core";

export type ButtonVariant =
  | "primary"
  | "danger"
  | "standard"
  | "outline"
  | "litePrimary"
  | "litePrimaryReverse";

@Component({
  selector: "oui-button",
  host: {
    class: "block min-w-fit",
    "[class.pointer-events-none]": "disabled()",
    "(click)": "click()",
  },
  template: `
    <button
      class="font-display inline-flex select-none items-center rounded-lg text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 {{
        sizeClass()
      }} {{ fullClass() }} {{ variantCompleteClass() }}"
      [type]="type()"
    >
      <ng-content select="prefix" />
      <ng-content />
      <ng-content select="suffix" />
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {
  type = input<"button" | "submit">("button");
  full = input(false, { transform: booleanAttribute });
  disabled = input(false, { transform: booleanAttribute });
  // @todo separate variant and color
  variant = input<ButtonVariant>("standard");

  size = input<"small" | "medium" | "large">("medium");
  href = input<string>();

  click() {
    if (!this.href() || this.disabled()) {
      return;
    }
    window.open(this.href(), "_blank");
  }

  sizeClass = computed(() => {
    switch (this.size()) {
      case "small":
        return "gap-2 px-3 py-1 text-xs";
      case "medium":
        return "gap-3 px-6 py-3";
      case "large":
        return "gap-3 px-8 py-5";
    }
  });

  fullClass = computed(() => {
    return this.full() ? "w-full justify-center" : "";
  });

  variantClass = computed(() => {
    switch (this.variant()) {
      case "primary":
        return "text-white focus-visible:outline-primary-500 to-primary-500 bg-gradient-227 from-[#4C7FFF] hover:from-[#4C6BCD] hover:to-[#1647CD]";
      case "danger":
        return "text-white bg-red-600 hover:bg-red-700 focus-visible:outline-red-600";
      case "standard":
        return "text-primary-500 hover:bg-primary-50 focus-visible bg-white font-medium";
      case "outline":
        return "text-primary-500 outline outline-1 outline-current";
      case "litePrimary":
        return "text-primary-500 focus-visible bg-primary-200 hover:text-primary-600 font-medium";
      case "litePrimaryReverse":
        return "text-white focus-visible bg-primary-500 font-semibold";
    }
  });

  variantCompleteClass = computed(() => {
    const disabledStyle = this.disabled()
      ? "pointer-events-none opacity-50"
      : "";
    return `${disabledStyle} ${this.variantClass()}`;
  });
}
